import React, {
  createContext,
  useContext,
  PropsWithChildren,
  useMemo,
  useEffect,
  useCallback,
  useReducer,
  useState,
} from 'react';
import { useMediaQuery } from '@rbx/ui';
import railReducer, { createInitialState, ScreenSize } from './RailReducer';

type TRailProviderContext = {
  primaryRailOpen: boolean;
  primaryRailCompact: boolean;
  drawerVariant: 'temporary' | 'persistent';
  hasSecondaryRail: boolean;
  allToolsOpen: boolean;
  learnOpen: boolean;
  isReady: boolean;
  setHasSecondaryRail: (collapsed: boolean) => void;
  setPrimaryRailCompact: (compact: boolean) => void;
  setPrimaryRailOpen: (open: boolean) => void;
  setAllToolsOpen: (open: boolean) => void;
  setLearnOpen: (open: boolean) => void;
};

const RailProviderContext = createContext<TRailProviderContext>({
  primaryRailOpen: false,
  primaryRailCompact: false,
  hasSecondaryRail: false,
  drawerVariant: 'persistent',
  learnOpen: false,
  allToolsOpen: false,
  isReady: false,
  setHasSecondaryRail: () => {},
  setPrimaryRailCompact: () => {},
  setPrimaryRailOpen: () => {},
  setAllToolsOpen: () => {},
  setLearnOpen: () => {},
});

export const useRailContext = () => {
  const context = useContext(RailProviderContext);

  return context;
};

export const RailProvider: React.FC<PropsWithChildren> = ({ children }) => {
  const isLessThenLarge = useMediaQuery((theme) => theme.breakpoints.down('Large'));
  const isLessThenMedium = useMediaQuery((theme) => theme.breakpoints.down('Medium'));
  const [isReady, setIsReady] = useState(false);

  let screenSize: ScreenSize = 'large';
  if (isLessThenMedium) {
    screenSize = 'small';
  } else if (isLessThenLarge) {
    screenSize = 'medium';
  }

  const [state, dispatch] = useReducer(
    railReducer,
    createInitialState({
      screenSize,
    }),
  );

  useEffect(() => {
    dispatch({ type: 'setScreenSize', payload: screenSize });
  }, [screenSize]);

  const setHasSecondaryRail = useCallback((value: boolean) => {
    dispatch({ type: 'setSecondaryRail', payload: value });
    // used to prevent animation on initial render
    setTimeout(() => {
      setIsReady(true);
    });
  }, []);

  const setLearnOpen = useCallback((open: boolean) => {
    dispatch({ type: 'setLearn', payload: open });
  }, []);

  const setAllToolsOpen = useCallback((open: boolean) => {
    dispatch({ type: 'setAllTools', payload: open });
  }, []);

  const setPrimaryRailOpen = useCallback((open: boolean) => {
    dispatch({ type: 'setPrimaryRailOpen', payload: open });
  }, []);

  const setPrimaryRailCompact = useCallback((value: boolean) => {
    dispatch({ type: 'setPrimaryRailCompact', payload: value });
  }, []);

  const value = useMemo(
    () => ({
      ...state,
      isReady,
      setHasSecondaryRail,
      setPrimaryRailCompact,
      setPrimaryRailOpen,
      setLearnOpen,
      setAllToolsOpen,
    }),
    [
      isReady,
      setAllToolsOpen,
      setHasSecondaryRail,
      setLearnOpen,
      setPrimaryRailCompact,
      setPrimaryRailOpen,
      state,
    ],
  );

  return <RailProviderContext.Provider value={value}>{children}</RailProviderContext.Provider>;
};
